import { RefObject, useCallback } from 'react';

interface UseDialogFormErrorNavigationOptions {
  containerRef?: RefObject<HTMLElement | null>;
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
}

const FOCUSABLE_SELECTOR =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

function isFocusableElement(element: HTMLElement): boolean {
  const tabIndex = element.getAttribute('tabindex');

  return (
    element.matches(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]',
    ) ||
    (tabIndex !== null && tabIndex !== '-1')
  );
}

function getFocusableTarget(element: HTMLElement): HTMLElement | null {
  if (isFocusableElement(element)) {
    return element;
  }

  return element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
}

export function useDialogFormErrorNavigation({
  containerRef,
  behavior = 'smooth',
  block = 'center',
  inline = 'nearest',
}: UseDialogFormErrorNavigationOptions = {}) {
  return useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const root: ParentNode = containerRef?.current ?? document;
      const firstInvalid = root.querySelector<HTMLElement>(
        '[aria-invalid="true"]',
      );

      if (!firstInvalid) {
        return;
      }

      firstInvalid.scrollIntoView({
        behavior,
        block,
        inline,
      });

      const focusTarget = getFocusableTarget(firstInvalid);
      focusTarget?.focus({ preventScroll: true });
    });
  }, [containerRef, behavior, block, inline]);
}
